import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { computeAudienceMix, computeUploadMix } from "@/components/BiasDonutsCard";
import { AUDIENCE_COLORS, audienceDisplayLabel } from "@/lib/unaffiliatedFmt";

const lbl = "mono text-[10px] uppercase tracking-[0.25em] text-zinc-500";
const pct = (n, t) => (t > 0 ? Math.round((n * 100) / t) : 0);

function Donut({ title, sub, rows, testid }) {
  const total = rows.reduce((s, r) => s + r.value, 0);
  const data = rows.filter((r) => r.value > 0);
  const top = data.reduce((b, r) => (r.value > (b?.value || 0) ? r : b), null);
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="flex items-center gap-4">
        <div className="relative shrink-0" style={{ width: 112, height: 112 }}>
          {total > 0 ? (
            <ResponsiveContainer>
              <PieChart><Pie data={data} dataKey="value" innerRadius={38} outerRadius={54} stroke="none" paddingAngle={2} isAnimationActive={false}>
                {data.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie></PieChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-zinc-600">—</div>}
          {top && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="mono text-base font-semibold text-white">{pct(top.value, total)}%</div>
              <div className="mono text-[8px] uppercase tracking-wider text-zinc-500">{top.name}</div>
            </div>
          )}
        </div>
        <div><div className={lbl}>{title}</div><div className="mono text-[11px] text-zinc-500 mt-1">{sub(total)}</div></div>
      </div>
      <div className="mt-4 space-y-1.5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center justify-between text-sm" data-testid={`${testid}-row-${r.name.toLowerCase().replace(/\W+/g, "-")}`}>
            <span className="flex items-center gap-2 text-zinc-300"><span className="w-2 h-2 rounded-sm" style={{ background: r.color }} />{r.name}</span>
            <span className="mono text-xs" style={{ color: r.color }}>{pct(r.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const C = { crypto: "#00F0FF", forex: "#A78BFA", tradfi: "#FBBF24", live: "#FBBF24", standard: "#00F0FF", shorts: "#FF4D8D" };

function mixRows(c, prof) {
  if (c.segment === "unaffiliated") {
    const counts = prof?.audience_mix?.counts || {};
    const up = prof?.windows?.all?.upload_mix || {};
    return {
      aud: Object.entries(counts).map(([k, v]) => ({ name: audienceDisplayLabel(k), value: v || 0, color: AUDIENCE_COLORS[k] || "#64748B" })),
      up: [{ name: "Livestream", value: up.live || 0, color: C.live }, { name: "Standard", value: up.normal || 0, color: C.standard }, { name: "Shorts", value: up.shorts || 0, color: C.shorts }],
    };
  }
  const vbc = prof?.videos_by_category || {};
  const ub = prof?.upload_bias || {};
  const a = computeAudienceMix({ crypto: +vbc.crypto || 0, forex: +vbc.forex || 0, nifty: +vbc.indian || 0 });
  const u = computeUploadMix({ live: +ub.live || 0, normal: +ub.normal || 0, shorts: +ub.shorts || 0 });
  const by = (segs, k) => (segs.find((s) => s.key === k) || {}).value || 0;
  return {
    aud: [{ name: "Crypto", value: by(a.segments, "crypto"), color: C.crypto }, { name: "Forex", value: by(a.segments, "forex"), color: C.forex }, { name: "TradFi", value: by(a.segments, "nifty"), color: C.tradfi }],
    up: [{ name: "Livestream", value: by(u.segments, "live"), color: C.live }, { name: "Standard", value: by(u.segments, "video"), color: C.standard }, { name: "Shorts", value: by(u.segments, "shorts"), color: C.shorts }],
  };
}

function exRows(prof) {
  const st = prof?.platforms_status || {};
  const out = [];
  for (const cat of ["crypto", "forex", "indian"]) {
    for (const [name, t] of Object.entries(st[cat] || {})) if (t && t.has_data) out.push({ name, cat, active: t.active !== false, count: t.count || 0, days: t.days_since_last });
  }
  return out.sort((a, b) => (b.active - a.active) || (b.count - a.count));
}

function ExRow({ e }) {
  const c = e.active ? "#39FF14" : "#F43F5E";
  return (
    <div data-testid={`lp-ex-${e.cat}-${e.name}`} data-status={e.active ? "active" : "stopped"} className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-md border" style={{ borderColor: `${c}66`, background: `${c}0d` }}>
      <span className="font-medium" style={{ color: c }}>{e.name}</span>
      <span className="mono text-[11px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.35)", color: c }}>{e.count.toLocaleString()} {e.count === 1 ? "video" : "videos"}</span>
      <span className="mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${c}40`, color: e.active ? "#0A0A0B" : "#F8FAFC" }}>{e.active ? "Promoting" : "Stopped"}</span>
      {e.days != null && <span className="mono text-[11px] text-zinc-400 ml-auto">Last promoted {e.days} day{e.days === 1 ? "" : "s"} ago</span>}
    </div>
  );
}

export function LiveMix({ c, prof }) {
  const m = mixRows(c, prof);
  const unaff = c.segment === "unaffiliated";
  const ex = unaff ? [] : exRows(prof);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-4">
      <section className="border hairline rounded-md p-6" style={{ background: "var(--surface)" }} data-testid="lp-mix">
        <div className="mono text-[11px] uppercase tracking-[0.3em] mb-5" style={{ color: "#00F0FF" }}>Audience &amp; upload mix</div>
        {!prof ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Donut testid="lp-donut-audience" title="Audience mix" sub={(t) => `${t.toLocaleString()} tagged videos`} rows={m.aud} />
            <Donut testid="lp-donut-upload" title="Upload mix" sub={(t) => `${t.toLocaleString()} ${unaff ? "videos" : "promo videos"}`} rows={m.up} />
          </div>
        )}
      </section>
      <section className="border hairline rounded-md p-6" style={{ background: "var(--surface)" }} data-testid="lp-exchanges">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
          <span className="mono text-[11px] uppercase tracking-[0.3em]" style={{ color: "#00F0FF" }}>Exchanges &amp; platforms</span>
          {!unaff && <span className="mono text-[11px] text-zinc-500">Green: promoting now · Red: stopped</span>}
        </div>
        <div className="flex flex-col gap-2">
          {unaff && (
            <div data-testid="lp-ex-unaffiliated" className="px-4 py-2.5 rounded-md border" style={{ borderColor: "rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.06)" }}>
              <span className="font-medium" style={{ color: "#FBBF24" }}>Unaffiliated</span>
              <span className="text-xs text-zinc-400 ml-3">No exchange promotion found</span>
            </div>
          )}
          {unaff && prof?.creator?.early_promo && (
            <div className="text-xs text-zinc-500 mt-1" data-testid="lp-early-promo">Early promo seen: <span className="text-zinc-400">{prof.creator.early_promo}</span></div>
          )}
          {!unaff && !prof && <div className="text-sm text-zinc-500">Loading…</div>}
          {!unaff && prof && !ex.length && <div className="text-sm text-zinc-500">No exchange promotion recorded.</div>}
          {ex.map((e) => <ExRow key={`${e.cat}-${e.name}`} e={e} />)}
        </div>
      </section>
    </div>
  );
}
